import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { Goal, GoalMonth, GoalPeriod } from '../api/types'
import { Icon } from '../components/Icon'
import { ErrorState, Loading } from '../components/States'
import { useToast } from '../components/Toast'
import { useAsync } from '../lib/useAsync'

// Setting a number is the easy half; knowing which number is the hard half. So each
// metric shows what the officer actually did in the last three months beside the box,
// with one-tap presets off those same figures — a target picked from the record rather
// than from the air. The metrics and the counting are unchanged (logic.GOAL_METRICS);
// this screen replaces the cramped modal that used to be the only way in.

const STEP: Record<string, number> = {
  conversations: 10, appointments: 1, applications: 1, new_contacts: 5, funded: 1,
}

/** What each number is for, in the officer's terms — the hint from the server says
 *  what it counts, this says why you'd aim at it. */
const WHY: Record<string, string> = {
  conversations: 'The one you control. Everything below it follows from this.',
  appointments: 'Where a lead becomes a conversation about a real loan.',
  applications: 'The number the month actually gets judged on.',
  new_contacts: 'Keeps the top of the book full for next month.',
  funded: 'The branch total, so you can see it even when none are yours.',
}

function average(months: GoalMonth[]): number {
  if (!months.length) return 0
  return Math.round(months.reduce((t, m) => t + m.n, 0) / months.length)
}

/** What the last few months actually say. Reporting the rounded average alone
 *  claimed "nothing recorded" for someone who funded one loan in August — one
 *  across three months rounds to zero, which is not the same as none. */
function historyLine(months: GoalMonth[]): string {
  const total = months.reduce((t, m) => t + m.n, 0)
  if (!total) return `Nothing recorded in the last ${months.length} months`
  const avg = average(months)
  if (avg >= 1) return `Averaging ${avg} a month`
  return `${total} in the last ${months.length} months`
}

export function Goals() {
  const { data, error, loading, reload } = useAsync(() => api.goals(), [])
  const [at, setAt] = useState(0)
  const [draft, setDraft] = useState<Record<string, Record<string, number>>>({})
  // Nothing to press. A number you typed is a number you meant, so it goes to the
  // server a beat after you stop typing and the line at the top says where it got to.
  const [saved, setSaved] = useState<'' | 'saving' | 'saved' | 'failed'>('')
  const pending = useRef<{ period: string; targets: Record<string, number> } | null>(null)
  const timer = useRef<number | null>(null)
  const toast = useToast()

  useEffect(() => {
    if (!data) return
    setDraft(Object.fromEntries(data.periods.map((p) =>
      [p.key, Object.fromEntries(p.goals.map((g) => [g.metric, g.target]))])))
  }, [data])

  async function flush() {
    const next = pending.current
    if (!next) return
    pending.current = null
    setSaved('saving')
    try {
      await api.setGoals(next.targets, next.period)
      setSaved('saved')
    } catch (e) {
      setSaved('failed')
      toast(errorText(e), { tone: 'danger' })
    }
  }

  /** Leaving the screen mid-keystroke should not lose the keystroke. */
  useEffect(() => () => {
    if (timer.current) {
      window.clearTimeout(timer.current)
      void flush()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const period: GoalPeriod | undefined = data?.periods[at]
  const values = (period && draft[period.key]) || {}
  const tracked = useMemo(() => Object.values(values).filter((n) => n > 0).length, [values])
  // Turning a goal off should not lose the number you had picked, so the last
  // non-zero value is kept here and put back when the eye is opened again.
  const [remembered, setRemembered] = useState<Record<string, number>>({})

  if (loading && !data) return <div className="page"><Loading /></div>
  if (error || !data) return <div className="page"><ErrorState error={error} onRetry={reload} /></div>

  const set = (metric: string, n: number) => {
    if (!period) return
    const clean = Math.max(0, Math.min(10000, Math.round(n) || 0))
    setDraft((d) => {
      const targets = { ...d[period.key], [metric]: clean }
      // Held for a moment so a number typed digit by digit is one save, not four.
      pending.current = { period: period.key, targets }
      if (timer.current) window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => { timer.current = null; void flush() }, 700)
      return { ...d, [period.key]: targets }
    })
  }

  /** The eye is the dashboard switch: a goal with a number shows on Today, a goal at
   *  zero does not. Closing it remembers the number; opening it suggests one from the
   *  officer's own last month rather than making them think of one. */
  function toggle(goal: Goal) {
    const current = values[goal.metric] ?? 0
    if (current > 0) {
      setRemembered((r) => ({ ...r, [goal.metric]: current }))
      return set(goal.metric, 0)
    }
    const history = data?.history[goal.metric] ?? []
    const suggestion = remembered[goal.metric] || history.at(-1)?.n || average(history) || 10
    set(goal.metric, suggestion)
  }

  return (
    <div className="page" style={{ maxWidth: 880 }}>
      <div className="page-head">
        <div>
          <h1 className="h1">Your goals</h1>
          <p className="subline">
            Pick a number for the ones you want to track and leave the rest at zero. Every figure here is
            counted from work recorded elsewhere — calls you logged, people you moved, contacts you added —
            so a goal can’t be hit without the work existing.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <SaveMark state={saved} />
          <Link to="/" className="btn">Back to Today</Link>
        </div>
      </div>

      {/* Which stretch of time you are aiming at. The month you are in, the two
          after it, and the half-year — a plan needs somewhere to live before the
          month it belongs to arrives. */}
      <div className="goal-periods" role="tablist">
        {data.periods.map((p, i) => {
          const n = Object.values(draft[p.key] ?? {}).filter((v) => v > 0).length
          return (
            <button key={p.key} role="tab" aria-selected={i === at}
              className={`goal-period${i === at ? ' on' : ''}`} onClick={() => setAt(i)}>
              <strong>{p.label}</strong>
              <span>{p.sub}</span>
              {n ? <i>{n} set</i> : null}
            </button>
          )
        })}
      </div>

      <div className="goal-setup">
        {(period?.goals ?? []).map((g) => (
          <GoalSetup key={g.metric} goal={g} history={data.history[g.metric] ?? []}
            value={values[g.metric] ?? 0} onChange={(n) => set(g.metric, n)}
            onToggle={() => toggle(g)} showsPace={period?.current ?? false} />
        ))}
      </div>

      <p className="quiet" style={{ fontSize: 12, marginTop: 18 }}>
        {tracked === 0
          ? 'Nothing is being tracked — the Today screen will offer to set goals again.'
          : `Tracking ${tracked} of ${period?.goals.length ?? 0} for ${period?.label ?? ''}.` +
            (period?.current ? ' They show on Today with how far through the month you are.' : '')}
      </p>
    </div>
  )
}

/** The whole save interface. It says what just happened and then gets out of the
 *  way — "Saved" sitting there forever reads as a button nobody pressed. */
function SaveMark({ state }: { state: '' | 'saving' | 'saved' | 'failed' }) {
  const [show, setShow] = useState(false)
  useEffect(() => {
    if (!state) return
    setShow(true)
    if (state !== 'saved') return
    const t = window.setTimeout(() => setShow(false), 2200)
    return () => window.clearTimeout(t)
  }, [state])
  if (!state || !show) return null
  return (
    <span className={`save-mark ${state}`} role="status">
      {state === 'saving' ? <><i className="save-dot" /> Saving…</>
        : state === 'failed' ? <><Icon name="error" size={15} /> Not saved</>
          : <><Icon name="check" size={15} /> Saved</>}
    </span>
  )
}

function GoalSetup({ goal, history, value, onChange, onToggle, showsPace }: {
  goal: Goal
  history: GoalMonth[]
  value: number
  onChange: (n: number) => void
  onToggle: () => void
  showsPace: boolean
}) {
  const shown = value > 0
  const step = STEP[goal.metric] ?? 1
  const best = history.reduce((b, m) => Math.max(b, m.n), 0)
  const last = history.at(-1)?.n ?? 0
  // Bars are scaled to the officer's own best month, not to the target being typed —
  // otherwise the history would shrink while you pick a number.
  const peak = Math.max(best, 1)

  // A preset only earns its place if it suggests something: "match last month" with
  // no last month is a button that sets zero.
  const presets: [string, number][] = [
    ...(last ? [['Match last month', last] as [string, number]] : []),
    ...(last ? [['Last month +10%', Math.ceil(last * 1.1)] as [string, number]] : []),
    ...(best && best !== last ? [['Your best month', best] as [string, number]] : []),
  ]

  return (
    <section className={`card goal-setup-card${shown ? '' : ' goal-off'}`}>
      <div className="card-body">
        <div className="goal-setup-head">
          <button className="goal-eye" onClick={onToggle} aria-pressed={shown}
            aria-label={`${shown ? 'Hide' : 'Show'} ${goal.label} on my dashboard`}>
            <Icon name={shown ? 'visibility' : 'visibility_off'} size={17} />
            <span>{shown ? 'On my dashboard' : 'Not on my dashboard'}</span>
          </button>
          <div>
            <h2 className="h2" style={{ fontSize: 16 }}>{goal.label}</h2>
            <p className="quiet" style={{ margin: '4px 0 0', fontSize: 12 }}>
              {goal.hint}. {WHY[goal.metric] ?? ''}
            </p>
          </div>
          <div className="goal-setup-input">
            <button className="iconbtn" aria-label={`Lower ${goal.label}`} disabled={value <= 0}
              onClick={() => onChange(value - step)}><Icon name="remove" size={17} /></button>
            <input className="field mono" type="number" min={0} max={10000} value={value || ''}
              placeholder="0" aria-label={`${goal.label} target`}
              onChange={(e) => onChange(Number(e.target.value))} />
            <button className="iconbtn" aria-label={`Raise ${goal.label}`}
              onClick={() => onChange(value + step)}><Icon name="add" size={17} /></button>
          </div>
        </div>

        <div className="goal-setup-foot">
          <div className="goal-history" aria-label={`${goal.label}: the last ${history.length} months`}>
            {history.map((m) => (
              <div key={m.month} className="goal-history-bar" title={`${m.label}: ${m.n}`}>
                <span className="goal-history-track"><i style={{ height: `${Math.round((m.n / peak) * 100)}%` }} /></span>
                <b>{m.n}</b>
                <small>{m.label}</small>
              </div>
            ))}
            <p className="quiet">
              {historyLine(history)}
              {showsPace && goal.done ? <> · <b>{goal.done}</b> so far this month</> : null}
              {!showsPace ? <> · hasn’t started yet</> : null}
            </p>
          </div>
          <div className="goal-presets">
            {presets.map(([label, n]) => (
              <button key={label} className="btn sm" onClick={() => onChange(n)}>{label} · {n}</button>
            ))}

          </div>
        </div>
      </div>
    </section>
  )
}
